/* Collection Gallery (New): lightbox for sections/collection-gallery-new.liquid.
   <collection-gallery> wires every tile button ([data-cg-index]) to the section's <dialog>.
   The dialog opens with showModal(): top layer (so the section fade-in transform can't
   trap it), page inert behind it. On top of that: Tab cycles inside, Esc / backdrop click /
   X close, ←/→ and swipe move (looping), neighbours preloaded, page scroll locked with the
   scrollbar width padded back (no shift), focus returns to the tile that opened it.
   Animations live in CSS under prefers-reduced-motion: no-preference. */
(function () {
  if (window.customElements.get('collection-gallery')) return;

  var SWIPE_MIN = 50;

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function lockScroll() {
    var html = document.documentElement;
    var body = document.body;
    var scrollbar = window.innerWidth - html.clientWidth;
    var saved = {
      htmlOverflow: html.style.overflow,
      bodyPaddingRight: body.style.paddingRight
    };
    if (scrollbar > 0) {
      body.style.paddingRight = (parseFloat(window.getComputedStyle(body).paddingRight) || 0) + scrollbar + 'px';
    }
    html.style.overflow = 'hidden';
    return saved;
  }

  function unlockScroll(saved) {
    if (!saved) return;
    document.documentElement.style.overflow = saved.htmlOverflow || '';
    document.body.style.paddingRight = saved.bodyPaddingRight || '';
  }

  class CollectionGallery extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-cg-dialog]');
      this.tiles = Array.prototype.slice.call(this.querySelectorAll('[data-cg-index]'));
      if (!this.dialog || !this.tiles.length || typeof this.dialog.showModal !== 'function') return;

      this.stage = this.dialog.querySelector('[data-cg-stage]');
      this.img = this.dialog.querySelector('[data-cg-img]');
      this.caption = this.dialog.querySelector('[data-cg-caption]');
      this.counter = this.dialog.querySelector('[data-cg-counter]');
      this.closeButton = this.dialog.querySelector('[data-cg-close]');
      this.current = 0;
      this.preloaded = {};

      this.onTileClick = this.onTileClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onClosed = this.onClosed.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onImgLoad = this.onImgLoad.bind(this);

      this.tiles.forEach(function (tile) {
        tile.addEventListener('click', this.onTileClick);
      }, this);
      this.dialog.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClosed);
      this.stage.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.stage.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.img.addEventListener('load', this.onImgLoad);
      this.img.addEventListener('error', this.onImgLoad);

      this.addControl('[data-cg-close]', this.close.bind(this));
      this.addControl('[data-cg-prev]', this.step.bind(this, -1));
      this.addControl('[data-cg-next]', this.step.bind(this, 1));
    }

    disconnectedCallback() {
      // Theme editor re-render / section unload while open: give the page its scroll back.
      if (this.saved) {
        unlockScroll(this.saved);
        this.saved = null;
      }
    }

    addControl(selector, handler) {
      var button = this.dialog.querySelector(selector);
      if (button) button.addEventListener('click', handler);
    }

    onTileClick(event) {
      var tile = event.currentTarget;
      this.open(parseInt(tile.getAttribute('data-cg-index'), 10) || 0, tile);
    }

    open(index, opener) {
      if (this.dialog.open) return;
      this.opener = opener || this.tiles[index];
      this.saved = lockScroll();
      this.show(index);
      this.dialog.showModal();
      this.closeButton.focus({ preventScroll: true });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    onCancel(event) {
      // Esc: one close path for every way out.
      event.preventDefault();
      this.close();
    }

    onClosed() {
      unlockScroll(this.saved);
      this.saved = null;
      this.img.removeAttribute('src');
      if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
    }

    step(delta) {
      this.show(this.current + delta);
    }

    show(index) {
      var total = this.tiles.length;
      index = ((index % total) + total) % total;
      this.current = index;

      var tile = this.tiles[index];
      var src = tile.getAttribute('data-cg-full');
      var alt = tile.getAttribute('data-cg-alt') || '';

      if (this.img.getAttribute('src') !== src) {
        this.img.classList.add('is-loading');
        // Ratio + delivered width (image_url caps at 2000) let the CSS size the box before the
        // file arrives, so the caption doesn't jump when it loads.
        var w = parseFloat(tile.getAttribute('data-cg-w'));
        var h = parseFloat(tile.getAttribute('data-cg-h'));
        if (w > 0 && h > 0) {
          this.img.style.setProperty('--cg-r', String(w / h));
          this.img.style.setProperty('--cg-dw', Math.min(w, 2000) + 'px');
        }
        this.img.setAttribute('src', src);
        if (this.img.complete) this.onImgLoad();
      }
      this.img.setAttribute('alt', alt);
      this.caption.textContent = alt;
      this.counter.textContent = pad(index + 1) + ' / ' + pad(total);

      this.preload(index + 1);
      this.preload(index - 1);
    }

    preload(index) {
      var total = this.tiles.length;
      if (total < 2) return;
      var src = this.tiles[((index % total) + total) % total].getAttribute('data-cg-full');
      if (!src || this.preloaded[src]) return;
      var image = new Image();
      image.decoding = 'async';
      image.src = src;
      this.preloaded[src] = image;
    }

    onImgLoad() {
      this.img.classList.remove('is-loading');
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      } else if (event.key === 'Tab') {
        this.trapTab(event);
      }
    }

    trapTab(event) {
      var focusables = Array.prototype.slice.call(this.dialog.querySelectorAll('button')).filter(function (el) {
        return el.getClientRects().length > 0;
      });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;
      if (event.shiftKey && (active === first || !this.dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    onDialogClick(event) {
      // Backdrop = the dialog itself or the empty stage around the figure.
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }
      if (event.target === this.dialog || event.target === this.stage) this.close();
    }

    onTouchStart(event) {
      if (event.touches.length !== 1) {
        this.touch = null;
        return;
      }
      this.touch = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }

    onTouchEnd(event) {
      this.suppressClick = false;
      if (!this.touch || !event.changedTouches.length) return;
      var dx = event.changedTouches[0].clientX - this.touch.x;
      var dy = event.changedTouches[0].clientY - this.touch.y;
      this.touch = null;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;
      this.suppressClick = true;
      this.step(dx < 0 ? 1 : -1);
    }
  }

  window.customElements.define('collection-gallery', CollectionGallery);
})();
